import { gql } from '@apollo/client';
import type { EventTrigger, Media, MediaType, TimestampInMillis, UUID } from '@cobliteam/common-types';
import type { VehicleTrackingSegmentType } from '../../__generated__/graphql';
import graphQLRequester from '../../request/GraphQLRequester';
import TimeZone from '../../request/Timezone';
import { toMedia } from '../camera/mediaTransformer';
import { getDeviceType } from '../device/DeviceTransformer';
import type { FullTrackingGraphQL, TrackingGraphQL, TrackingSpeedEvents } from './Types';

// This odd response type mixes transformed (mediaEvents) + non-transformed results. Doing this way we to benefit from the
// existing type structure created for Media (especially widely used enum conversions), although it's not the recommended path.
export type VehicleTrackingSegment = Omit<VehicleTrackingSegmentType, 'mediaEvents'> & { mediaEvents: Media[]; mediaId?: UUID };

export class GrahpQLTrackingRequester {
  // TO BE REMOVED: it fetches all tracking info and a user might not have the permission
  // to read the speed event events, for example
  public getFullTrackingInfo = (
    vehicleId: UUID,
    startTimestamp: TimestampInMillis,
    endTimestamp: TimestampInMillis,
    useMapMatch: boolean,
    withSpeed = false
  ): Promise<FullTrackingGraphQL> => {
    const operationName = 'getFullTrackingInfo';
    const fullQuery = `
      vehicleId
      drivers {
        name
        id
      }
      pathGeojson {
        type
        geometry {
          type
          coordinates
        }
        properties {
          timestamps
          speeds
        }
      }
      roadSpeedEvents {
        speedLimitInKmh
        speedInKmh
        severity
        durationAboveInMillis
        driverName
        eventTime
        type
        location {
          lat
          lng
        }
      }
      vehicleSpeedEvents {
        speedLimitInKmh
        speedInKmh
        severity
        durationAboveInMillis
        driverName
        eventTime
        type
        location {
          lat
          lng
        }
      }
      stops {
        startTime
        endTime
        driverName
        idleOnInMillis
        idleOffInMillis
        hasFilteredIdleOnTime
        location {
          lat
          lng
        }
        geofences {
          name
          category
        }
      }
      summary {
        totalFuelCost
        totalDistance
        totalDuration
        totalStops
        totalSpeedEvents
      }
      incidents {
        type
        time
        latitude
        longitude
      }
      vehicle {
        id
        licensePlate
        brand
        model
        fuelConsumption
        fuelPrice
      }
      device {
        type
        cobliId
        lastLocation {
          isPlugged
          pluggedUpdateTimestamp
        }
      }
    `;
    return this.execQuery<FullTrackingGraphQL>(operationName, vehicleId, startTimestamp, endTimestamp, useMapMatch, fullQuery, withSpeed);
  };

  public getTrackingEssencialInfo = (
    vehicleId: UUID,
    startTimestamp: TimestampInMillis,
    endTimestamp: TimestampInMillis,
    useMapMatch: boolean,
    withSpeed = false
  ): Promise<TrackingGraphQL> => {
    const operationName = 'getTrackingEssencialInfo';
    const fullQuery = `
      vehicleId
      pathGeojson {
        type
        geometry {
          type
          coordinates
        }
        properties {
          timestamps
          speeds
        }
      }
      stops {
        startTime
        endTime
        driverName
        idleOnInMillis
        idleOffInMillis
        hasFilteredIdleOnTime
        location {
          lat
          lng
        }
        geofences {
          name
          category
        }
      }
      summary {
        totalFuelCost
        totalDistance
        totalDuration
        totalStops
      }
      incidents {
        type
        time
        latitude
        longitude
      }
      vehicle {
        id
        licensePlate
        brand
        model
        fuelConsumption
        fuelPrice
      }
      device {
        type
        cobliId
        lastLocation {
          isPlugged
          pluggedUpdateTimestamp
        }
      }
    `;
    return this.execQuery<TrackingGraphQL>(operationName, vehicleId, startTimestamp, endTimestamp, useMapMatch, fullQuery, withSpeed).then((tracking) => ({
      ...tracking,
      device: tracking.device
        ? {
            ...tracking.device,
            type: getDeviceType(tracking.device.type),
          }
        : undefined,
    }));
  };

  public getTrackingSpeedEvents = (vehicleId: UUID, startTimestamp: TimestampInMillis, endTimestamp: TimestampInMillis, useMapMatch: boolean): Promise<TrackingSpeedEvents> => {
    const operationName = 'getTrackingEvents';
    const query = `
      roadSpeedEvents {
        speedLimitInKmh
        speedInKmh
        severity
        durationAboveInMillis
        driverName
        eventTime
        type
        location {
          lat
          lng
        }
        startTime
        medianSpeedInKmh
        medianBasedSeverity
      }
      vehicleSpeedEvents {
        speedLimitInKmh
        speedInKmh
        severity
        durationAboveInMillis
        driverName
        eventTime
        type
        location {
          lat
          lng
        }
        startTime
        medianSpeedInKmh
        medianBasedSeverity
      }
      geofenceEvents {
        id
        time
        lng
        lat
        eventType
        geofence {
            id
            name
            category
        }
      }
      summary {
        totalSpeedEvents
      }
    `;

    return this.execQuery<TrackingSpeedEvents>(operationName, vehicleId, startTimestamp, endTimestamp, useMapMatch, query);
  };

  private execQuery = <T>(
    operationName: string,
    vehicleId: UUID,
    startTimestamp: TimestampInMillis,
    endTimestamp: TimestampInMillis,
    useMapMatch: boolean,
    query: string,
    withSpeed = false
  ): Promise<any> => {
    const timeZone = TimeZone.getTimezone();

    return graphQLRequester
      .query<{ vehicleTracking: T }>({
        query: gql`
          query ${operationName}($trackingInput: VehicleTrackingInput) {
            vehicleTracking(input: $trackingInput) {
              ${query}
            }
          }`,
        variables: {
          trackingInput: {
            vehicle_id: vehicleId,
            start_time: startTimestamp,
            end_time: endTimestamp,
            tz: timeZone,
            match: useMapMatch,
            with_speed: withSpeed,
          },
        },
      })
      .then((graphQlResponse) => graphQlResponse.vehicleTracking);
  };

  public async getVehicleTrackingSegment(
    vehicleId: UUID,
    startTime: number,
    endTime: number,
    segmentStart: number,
    segmentEnd: number,
    allowUnfinishedStep: boolean,
    match: boolean,
    eventTriggers?: EventTrigger[],
    mediaTypes?: MediaType[]
  ): Promise<VehicleTrackingSegment> {
    const tz = TimeZone.getTimezone();

    const query = gql`
      query vehicleTrackingSegment($input: VehicleTrackingSegmentInput!, $event_triggers: [EventTrigger], $media_types: [MediaType]) {
        vehicleTrackingSegment(input: $input) {
          start_time
          end_time
          has_route_steps
          tracking {
            vehicleId
            pathGeojson {
              type
              geometry {
                type
                coordinates
              }
              properties {
                timestamps
              }
            }
          }
          mediaEvents(
            event_triggers: $event_triggers,
            media_types: $media_types
          ) {
            id
            media_type
            timestamp
            severity
            media_details {
              ... on EventVideo {
                event_trigger
                latitude
                longitude
              }
            }
          }
        }
      }
    `;

    const { vehicleTrackingSegment } = await graphQLRequester.query<{ vehicleTrackingSegment: VehicleTrackingSegmentType }>({
      query,
      variables: {
        input: {
          vehicle_id: vehicleId,
          start_time: startTime,
          end_time: endTime,
          segment_start: segmentStart,
          segment_end: segmentEnd,
          allow_unfinished_step: allowUnfinishedStep,
          tz,
          match,
        },
        event_triggers: eventTriggers,
        media_types: mediaTypes,
      },
    });

    return {
      ...vehicleTrackingSegment,
      mediaEvents: (vehicleTrackingSegment.mediaEvents as any[]).map(toMedia),
    };
  }
}

export default GrahpQLTrackingRequester;
